// C:\IntegraNube_WebStore\src\components\Header.js
'use strict';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Link as RouterLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Popper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import {
  ArrowForwardRounded as ArrowForwardIcon,
  CategoryOutlined as CategoryIcon,
  ClearOutlined as ClearIcon,
  ContactSupportOutlined as ContactIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  HomeOutlined as HomeIcon,
  ImageNotSupportedOutlined as NoImageIcon,
  Inventory2Outlined as ProductsIcon,
  SearchOutlined as SearchIcon,
  SellOutlined as BrandsIcon,
  ShoppingCartOutlined as ShoppingCartIcon,
  StarRounded as StarIcon,
  StorefrontOutlined as StorefrontIcon,
} from '@mui/icons-material';

import api from '../api/api';
import mercadoLibreIcon from '../assets/mercadolibre.png';
import BotonContactoFlotante from './BotonContactoFlotante';

const CART_STORAGE_KEY = 'integranube_webstore_cart';
const CART_UPDATED_EVENT = 'integranube-cart-updated';

function getCartItemQuantity(item) {
  const number = Number(
    item?.cantidad ??
    item?.quantity ??
    0
  );

  return Number.isFinite(number) && number > 0
    ? number
    : 0;
}

function countCartUnits(items) {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.reduce(
    (total, item) =>
      total + getCartItemQuantity(item),
    0
  );
}

function readCartCount() {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    const raw =
      window.localStorage.getItem(
        CART_STORAGE_KEY
      );

    if (!raw) {
      return 0;
    }

    return countCartUnits(
      JSON.parse(raw)
    );
  } catch (error) {
    console.warn(
      '[Header] No fue posible leer el carrito:',
      error
    );

    return 0;
  }
}

/* ===================================================================== */
/*  Helpers tienda                                                       */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') return {};

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

function safeColor(value, fallback) {
  const color = String(value || '').trim();
  return color || fallback;
}

function normalizeMercadoLibreStoreUrl(value) {
  const raw =
    String(value || '').trim();

  if (!raw) {
    return null;
  }

  try {
    const url =
      new URL(raw);

    if (
      url.protocol !== 'http:' &&
      url.protocol !== 'https:'
    ) {
      return null;
    }

    const hostname =
      String(
        url.hostname || ''
      )
        .trim()
        .toLowerCase();

    /*
     * Sólo aceptamos dominios oficiales de Mercado Libre.
     * El endpoint ya valida esto, pero repetimos la validación
     * en el frontend antes de abrir un enlace externo.
     */
    if (
      !/(^|\.)mercadolibre\.[a-z.]+$/i
        .test(hostname)
    ) {
      return null;
    }

    url.protocol = 'https:';

    return url.toString();
  } catch (_) {
    return null;
  }
}

/* ===================================================================== */
/*  Helpers páginas públicas                                            */
/* ===================================================================== */

function extractPublicPages(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.paginas)) return payload.paginas;
  if (Array.isArray(payload?.data?.paginas)) {
    return payload.data.paginas;
  }
  return [];
}

function normalizePublicPages(items) {
  const seen = new Set();

  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const slug = String(item?.slug || '')
        .trim()
        .toLowerCase();
      const titulo = String(item?.titulo || '').trim();

      if (
        !slug ||
        !titulo ||
        slug.includes('/') ||
        slug.includes('.') ||
        seen.has(slug)
      ) {
        return null;
      }

      seen.add(slug);

      return {
        id: item?.id ?? slug,
        slug,
        titulo,
        orden: Number.isFinite(Number(item?.orden))
          ? Number(item.orden)
          : 0,
      };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        a.orden - b.orden ||
        a.titulo.localeCompare(b.titulo, 'es', {
          sensitivity: 'base',
        })
    );
}

function compactPageLabel(page) {
  const slug = String(page?.slug || '').toLowerCase();

  if (slug === 'despachos') return 'Envíos y despachos';
  if (slug === 'cambios-devoluciones') return 'Cambios y garantía';
  if (slug === 'terminos-y-condiciones') return 'Términos y condiciones';

  return String(page?.titulo || '').trim();
}


function extractCategories(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.categorias)) return payload.categorias;
  if (Array.isArray(payload?.data?.categorias)) return payload.data.categorias;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
}

function extractBrands(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.marcas)) return payload.marcas;
  if (Array.isArray(payload?.data?.marcas)) return payload.data.marcas;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
}

function normalizeCatalogNavigation(items, type) {
  const seen = new Set();

  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const nombre = String(
        item?.nombre ||
        item?.titulo ||
        item?.name ||
        ''
      ).trim();

      const slug = String(
        item?.slug ||
        ''
      ).trim();

      if (!nombre || !slug || seen.has(slug.toLowerCase())) {
        return null;
      }

      seen.add(slug.toLowerCase());

      const rawPath = String(
        item?.ruta ||
        item?.path ||
        ''
      ).trim();

      const fallbackPrefix =
        type === 'brand'
          ? '/marca/'
          : '/categoria/';

      const path =
        rawPath.startsWith('/')
          ? rawPath
          : `${fallbackPrefix}${encodeURIComponent(slug)}`;

      const productosRaw =
        item?.productos ??
        item?.productos_count ??
        item?.cantidad_productos ??
        item?.total_productos ??
        0;

      const productosNumero =
        Number(productosRaw);

      const productos =
        Number.isFinite(productosNumero) &&
        productosNumero > 0
          ? productosNumero
          : 0;

      return {
        id: item?.id ?? `${type}:${slug}`,
        nombre,
        slug,
        path,
        productos,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      /*
       * Dropdown del Header:
       * primero las categorías/marcas con mayor cantidad de productos.
       *
       * No usamos orden_ecommerce aquí porque el objetivo de este menú
       * rápido es mostrar automáticamente lo más relevante del catálogo.
       */
      if (b.productos !== a.productos) {
        return b.productos - a.productos;
      }

      return a.nombre.localeCompare(
        b.nombre,
        'es',
        { sensitivity: 'base' }
      );
    });
}

/* ===================================================================== */
/*  Helpers búsqueda                                                     */
/* ===================================================================== */

function extractProducts(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.productos)) {
    return payload.productos;
  }

  if (Array.isArray(payload?.data?.productos)) {
    return payload.data.productos;
  }

  return [];
}

function getProductImage(product) {
  const candidates = [
    product?.imagen,
    product?.imagen_url,
    product?.imagenUrl,
    product?.thumbnail,
  ];

  for (const candidate of candidates) {
    if (
      typeof candidate === 'string' &&
      candidate.trim()
    ) {
      return candidate.trim();
    }
  }

  if (
    Array.isArray(product?.imagenes) &&
    product.imagenes.length
  ) {
    const first = product.imagenes[0];

    if (
      typeof first === 'string' &&
      first.trim()
    ) {
      return first.trim();
    }

    if (
      first &&
      typeof first === 'object'
    ) {
      return (
        first.url ||
        first.url_publica ||
        first.src ||
        null
      );
    }
  }

  return null;
}

function formatPrice(
  value,
  moneda = 'CLP',
  locale = 'es-CL'
) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(
      locale || 'es-CL',
      {
        style: 'currency',
        currency:
          moneda || 'CLP',
        maximumFractionDigits:
          moneda === 'CLP'
            ? 0
            : 2,
      }
    ).format(number);
  } catch (_) {
    return `$${Math.round(
      number
    ).toLocaleString(
      locale || 'es-CL'
    )}`;
  }
}

function formatUnit(value, pesable = false) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (!raw) {
    return pesable
      ? 'Kilogramo'
      : 'Unidad';
  }

  const map = {
    un: 'Unidad',
    und: 'Unidad',
    u: 'Unidad',
    unidad: 'Unidad',
    unidades: 'Unidad',

    kg: 'Kilogramo',
    kgs: 'Kilogramo',
    kilo: 'Kilogramo',
    kilos: 'Kilogramo',
    kilogramo: 'Kilogramo',
    kilogramos: 'Kilogramo',

    g: 'Gramo',
    gr: 'Gramo',
    grs: 'Gramo',
    gramo: 'Gramo',
    gramos: 'Gramo',

    l: 'Litro',
    lt: 'Litro',
    litro: 'Litro',
    litros: 'Litro',

    ml: 'Mililitro',
    mililitro: 'Mililitro',

    m: 'Metro',
    mt: 'Metro',
    metro: 'Metro',

    caja: 'Caja',
    pack: 'Pack',
    paquete: 'Paquete',
    bolsa: 'Bolsa',
    botella: 'Botella',
  };

  if (map[raw]) {
    return map[raw];
  }

  if (
    pesable &&
    ['unidad', 'un', 'und', 'u'].includes(raw)
  ) {
    return 'Kilogramo';
  }

  return (
    raw.charAt(0).toUpperCase() +
    raw.slice(1)
  );
}

/* ===================================================================== */
/*  Resultado búsqueda                                                   */
/* ===================================================================== */

function SearchResult({
  product,
  colorPrimario,
  colorTexto,
  colorTextoSecundario,
  colorOferta,
  monedaDefault,
  locale,
  mostrarSku,
  mostrarUnidad,
  mostrarPrecioNormalOferta,
  mostrarPorcentajeOferta,
  onSelect,
}) {
  const imageUrl =
    getProductImage(product);

  const nombre =
    String(
      product?.nombre ||
      'Producto'
    ).trim();

  const sku =
    String(
      product?.sku || ''
    ).trim();

  const precioNumero =
    Number(
      product?.precio ??
      product?.precio_bruto ??
      product?.precioNeto ??
      product?.precio_neto
    );

  const precioNormalNumero =
    Number(
      product?.precioNormal ??
      product?.precio_normal ??
      product?.precioNormalDetalle?.bruto
    );

  const tieneOferta =
    Number.isFinite(precioNumero) &&
    precioNumero > 0 &&
    Number.isFinite(precioNormalNumero) &&
    precioNormalNumero > precioNumero;

  const descuentoPorcentaje =
    tieneOferta
      ? Math.max(
          1,
          Math.round(
            (
              (precioNormalNumero - precioNumero) /
              precioNormalNumero
            ) * 100
          )
        )
      : 0;

  const monedaProducto =
    product?.moneda ||
    monedaDefault ||
    'CLP';

  const precio =
    formatPrice(
      precioNumero,
      monedaProducto,
      locale
    );

  const precioNormal =
    tieneOferta &&
    mostrarPrecioNormalOferta
      ? formatPrice(
          precioNormalNumero,
          monedaProducto,
          locale
        )
      : null;

  const unidad =
    formatUnit(
      product?.unidad,
      product?.pesable === true
    );

  const disponible =
    product?.disponible !== false;

  const destacado =
    product?.destacado === true;

  const [imageError, setImageError] =
    useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        appearance: 'none',
        border: 0,
        bgcolor: 'transparent',
        width: '100%',
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        textAlign: 'left',
        px: 1.5,
        py: 1.15,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',

        '&:hover': {
          bgcolor: '#f8fafc',
        },

        '&:focus-visible': {
          outline:
            `2px solid ${colorPrimario}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box
        sx={{
          width: 54,
          height: 54,
          flexShrink: 0,
          borderRadius: 1.5,
          border: '1px solid #e5e7eb',
          bgcolor: '#fff',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {imageUrl && !imageError ? (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            loading="lazy"
            onError={() =>
              setImageError(true)
            }
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              p: 0.4,
            }}
          />
        ) : (
          <NoImageIcon
            sx={{
              color: '#cbd5e1',
              fontSize: 26,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              color: colorTexto,
              fontSize: 14,
              lineHeight: 1.3,
              fontWeight: 800,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nombre}
          </Typography>

          {destacado ? (
            <StarIcon
              sx={{
                color: colorPrimario,
                fontSize: 17,
                flexShrink: 0,
              }}
            />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ mt: 0.35 }}
        >
          {precio ? (
            <Stack
              direction="row"
              spacing={0.65}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              {precioNormal ? (
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      colorTextoSecundario,
                    fontWeight: 700,
                    opacity: 0.72,
                    textDecoration: 'line-through',
                  }}
                >
                  {precioNormal}
                </Typography>
              ) : null}

              <Typography
                sx={{
                  color: disponible
                    ? colorPrimario
                    : colorTextoSecundario,
                  fontSize: 14,
                  fontWeight: 900,
                }}
              >
                {precio}{' '}
                {mostrarUnidad ? (
                  <Box
                    component="span"
                    sx={{
                      color:
                        colorTextoSecundario,
                      fontSize: 12,
                      fontWeight: 700,
                      opacity: 0.72,
                    }}
                  >
                    / {unidad}
                  </Box>
                ) : null}
              </Typography>

              {tieneOferta &&
              mostrarPorcentajeOferta ? (
                <Chip
                  label={`${descuentoPorcentaje}% OFF`}
                  size="small"
                  sx={{
                    height: 20,
                    bgcolor:
                      `${colorOferta}12`,
                    color:
                      colorOferta,
                    border:
                      `1px solid ${colorOferta}22`,
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                />
              ) : null}
            </Stack>
          ) : null}

          {mostrarSku && sku ? (
            <Typography
              variant="caption"
              sx={{
                color:
                  colorTextoSecundario,
                opacity: 0.72,
                fontWeight: 700,
              }}
            >
              SKU {sku}
            </Typography>
          ) : null}

          {!disponible ? (
            <Chip
              label="Agotado"
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 800,
              }}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

/* ===================================================================== */
/*  Header                                                               */
/* ===================================================================== */

export default function Header({
  tienda = null,
  config = null,
  storeConfig = null,
  cartCount = null,
  onSearch = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile =
    useMediaQuery(
      '(max-width:700px)'
    );

  const isTablet =
    useMediaQuery(
      '(max-width:1050px)'
    );

  const source =
    tienda ||
    config ||
    storeConfig ||
    {};

  const data =
    useMemo(
      () =>
        resolveTiendaData(source),
      [source]
    );

  const nombre =
    String(
      data?.nombre ||
      'Tienda Online'
    ).trim();

  const logoUrl =
    String(
      data?.branding?.logo ||
      ''
    ).trim();

  const colorPrimario =
    safeColor(
      data?.branding?.colorPrimario,
      '#1976d2'
    );

  const colorSuperficie =
    safeColor(
      data?.branding?.colorSuperficie,
      '#ffffff'
    );

  const colorFondo =
    safeColor(
      data?.branding?.colorFondo,
      '#f8fafc'
    );

  const colorTexto =
    safeColor(
      data?.branding?.colorTexto,
      '#111827'
    );

  const colorTextoSecundario =
    safeColor(
      data?.branding
        ?.colorTextoSecundario,
      '#6b7280'
    );

  const colorOferta =
    safeColor(
      data?.branding?.colorOferta,
      '#dc2626'
    );

  const moneda =
    String(
      data?.regionalizacion?.moneda ||
      'CLP'
    )
      .trim()
      .toUpperCase();

  const locale =
    String(
      data?.regionalizacion?.locale ||
      'es-CL'
    ).trim();

  const buscadorHabilitado =
    data?.catalogo?.buscador !== false;

  const mostrarUnidad =
    data?.catalogo?.mostrarUnidad !== false;

  const mostrarPrecioNormalOferta =
    data?.ofertas
      ?.mostrarPrecioNormal !== false;

  const mostrarPorcentajeOferta =
    data?.ofertas
      ?.mostrarPorcentaje !== false;

  const barraAvisoHabilitada =
    data?.barraAviso?.habilitada === true &&
    Boolean(
      String(
        data?.barraAviso?.texto || ''
      ).trim()
    );

  const barraAvisoTexto =
    String(
      data?.barraAviso?.texto || ''
    ).trim();

  const barraAvisoEnlace =
    String(
      data?.barraAviso?.enlace || ''
    ).trim();


  const carritoHabilitado =
    data?.funciones?.carrito !== false;

  const contactoHabilitado =
    data?.funciones?.contacto !== false;

  const seguimientoHabilitado =
    data?.funciones?.seguimientoPedidos !== false;

  const mostrarSku =
    data?.ecommerce?.mostrarSku !== false;

  const mercadoLibreActivo =
    data?.integraciones
      ?.mercadolibre
      ?.activa === true;

  const mercadoLibreUrlTienda =
    normalizeMercadoLibreStoreUrl(
      data?.integraciones
        ?.mercadolibre
        ?.urlTienda
    );

  const mostrarMercadoLibre =
    mercadoLibreActivo &&
    Boolean(mercadoLibreUrlTienda);

  const [logoError, setLogoError] =
    useState(false);

  const [
    internalCartCount,
    setInternalCartCount,
  ] = useState(() => {
    const stored =
      readCartCount();

    if (stored > 0) {
      return stored;
    }

    const provided =
      Number(cartCount);

    return Number.isFinite(provided) && provided > 0
      ? provided
      : 0;
  });

  const [searchText, setSearchText] =
    useState('');

  const [
    searchResults,
    setSearchResults,
  ] = useState([]);

  const [
    searchLoading,
    setSearchLoading,
  ] = useState(false);

  const [
    searchOpen,
    setSearchOpen,
  ] = useState(false);

  const [
    publicPages,
    setPublicPages,
  ] = useState([]);

  const [
    categoryItems,
    setCategoryItems,
  ] = useState([]);

  const [
    brandItems,
    setBrandItems,
  ] = useState([]);

  const [
    catalogNavLoading,
    setCatalogNavLoading,
  ] = useState(true);

  const [
    categoryAnchorEl,
    setCategoryAnchorEl,
  ] = useState(null);

  const [
    brandAnchorEl,
    setBrandAnchorEl,
  ] = useState(null);

  const catalogCloseTimer =
    useRef(null);

  const searchSequence =
    useRef(0);

  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  useEffect(() => {
    const controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;

    let mounted = true;

    async function loadPublicPages() {
      try {
        const response = await api.get(
          '/paginas',
          controller
            ? {
                signal: controller.signal,
                timeout: 6000,
                retries: 1,
              }
            : {
                timeout: 6000,
                retries: 1,
              }
        );

        if (!mounted) return;

        setPublicPages(
          normalizePublicPages(
            extractPublicPages(response?.data)
          )
        );
      } catch (error) {
        if (
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        console.warn(
          '[Header] No fue posible cargar páginas públicas:',
          error?.message || error
        );

        if (mounted) {
          setPublicPages([]);
        }
      }
    }

    loadPublicPages();

    return () => {
      mounted = false;
      controller?.abort?.();
    };
  }, []);


  useEffect(() => {
    const controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;

    let mounted = true;

    async function loadCatalogNavigation() {
      setCatalogNavLoading(true);

      const requestConfig =
        controller
          ? {
              signal: controller.signal,
              timeout: 6000,
              retries: 1,
            }
          : {
              timeout: 6000,
              retries: 1,
            };

      try {
        const [
          categoriesResult,
          brandsResult,
        ] = await Promise.allSettled([
          api.get('/categorias', requestConfig),
          api.get('/marcas', requestConfig),
        ]);

        if (!mounted) return;

        if (categoriesResult.status === 'fulfilled') {
          setCategoryItems(
            normalizeCatalogNavigation(
              extractCategories(
                categoriesResult.value?.data
              ),
              'category'
            )
          );
        } else {
          setCategoryItems([]);
          console.warn(
            '[Header] No fue posible cargar categorías:',
            categoriesResult.reason?.message ||
            categoriesResult.reason
          );
        }

        if (brandsResult.status === 'fulfilled') {
          setBrandItems(
            normalizeCatalogNavigation(
              extractBrands(
                brandsResult.value?.data
              ),
              'brand'
            )
          );
        } else {
          setBrandItems([]);
          console.warn(
            '[Header] No fue posible cargar marcas:',
            brandsResult.reason?.message ||
            brandsResult.reason
          );
        }
      } finally {
        if (mounted) {
          setCatalogNavLoading(false);
        }
      }
    }

    loadCatalogNavigation();

    return () => {
      mounted = false;
      controller?.abort?.();
    };
  }, []);

  useEffect(() => {
    setCategoryAnchorEl(null);
    setBrandAnchorEl(null);
  }, [location.pathname]);

  useEffect(() => {
    const refreshCartCount = (
      event = null
    ) => {
      const eventItems =
        event?.detail?.items ??
        event?.detail;

      if (Array.isArray(eventItems)) {
        setInternalCartCount(
          countCartUnits(eventItems)
        );
        return;
      }

      setInternalCartCount(
        readCartCount()
      );
    };

    refreshCartCount();

    const handleStorage = (event) => {
      if (
        !event.key ||
        event.key === CART_STORAGE_KEY
      ) {
        refreshCartCount();
      }
    };

    window.addEventListener(
      CART_UPDATED_EVENT,
      refreshCartCount
    );

    window.addEventListener(
      'storage',
      handleStorage
    );

    return () => {
      window.removeEventListener(
        CART_UPDATED_EVENT,
        refreshCartCount
      );

      window.removeEventListener(
        'storage',
        handleStorage
      );
    };
  }, []);

  const displayedCartCount =
    Math.max(
      0,
      Math.floor(
        Number(internalCartCount) || 0
      )
    );

  const routeIs = (path) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path ||
        location.pathname.startsWith(
          `${path}/`
        );


  const navButtonSx = (active = false) => ({
    minHeight: 40,
    minWidth: 0,
    px: 1.05,
    borderRadius: 2.25,
    textTransform: 'none',
    fontSize: 13.5,
    lineHeight: 1,
    fontWeight: active ? 900 : 800,
    color: active
      ? colorPrimario
      : colorTexto,
    bgcolor: active
      ? `${colorPrimario}0d`
      : 'transparent',
    transition:
      'background-color 140ms ease, color 140ms ease, transform 140ms ease',

    '& .MuiButton-startIcon': {
      mr: 0.7,
      color: active
        ? colorPrimario
        : colorTextoSecundario,
    },

    '& .MuiButton-endIcon': {
      ml: 0.35,
      color: active
        ? colorPrimario
        : colorTextoSecundario,
      transition: 'transform 140ms ease',
    },

    '&:hover': {
      bgcolor: active
        ? `${colorPrimario}14`
        : colorFondo,
      color: colorPrimario,
    },
  });

  const categoriesOpen =
    Boolean(categoryAnchorEl);

  const brandsOpen =
    Boolean(brandAnchorEl);

  const cancelCatalogClose = () => {
    if (
      catalogCloseTimer.current
    ) {
      window.clearTimeout(
        catalogCloseTimer.current
      );

      catalogCloseTimer.current = null;
    }
  };

  const closeCatalogMenus = () => {
    cancelCatalogClose();
    setCategoryAnchorEl(null);
    setBrandAnchorEl(null);
  };

  const scheduleCatalogClose = () => {
    cancelCatalogClose();

    catalogCloseTimer.current =
      window.setTimeout(
        () => {
          setCategoryAnchorEl(null);
          setBrandAnchorEl(null);
          catalogCloseTimer.current = null;
        },
        180
      );
  };

  const openCategories = (event) => {
    cancelCatalogClose();
    setBrandAnchorEl(null);
    setCategoryAnchorEl(
      event.currentTarget
    );
  };

  const openBrands = (event) => {
    cancelCatalogClose();
    setCategoryAnchorEl(null);
    setBrandAnchorEl(
      event.currentTarget
    );
  };

  useEffect(() => {
    return () => {
      if (
        catalogCloseTimer.current
      ) {
        window.clearTimeout(
          catalogCloseTimer.current
        );
      }
    };
  }, []);

  const dropdownPaperSx = {
    mt: 1,
    width: 440,
    maxWidth: 'calc(100vw - 24px)',
    p: 1.25,
    borderRadius: 3,
    border: '1px solid #e5e7eb',
    bgcolor: colorSuperficie,
    color: colorTexto,
    boxShadow:
      '0 24px 70px rgba(15, 23, 42, 0.16)',
    overflow: 'hidden',
  };

  const dropdownItemSx = {
    justifyContent: 'flex-start',
    textAlign: 'left',
    minWidth: 0,
    width: '100%',
    px: 1.1,
    py: 0.9,
    borderRadius: 2,
    textTransform: 'none',
    color: colorTexto,
    fontSize: 13.5,
    fontWeight: 750,
    lineHeight: 1.25,

    '&:hover': {
      bgcolor: `${colorPrimario}0b`,
      color: colorPrimario,
    },
  };

  /* =================================================================== */
  /*  Búsqueda predictiva                                                */
  /* =================================================================== */

  useEffect(() => {
    if (!buscadorHabilitado) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchOpen(false);
      return undefined;
    }

    const texto =
      String(searchText || '')
        .trim();

    if (texto.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    const sequence =
      ++searchSequence.current;

    const controller =
      new AbortController();

    const timer =
      window.setTimeout(
        async () => {
          setSearchLoading(true);

          try {
            const response =
              await api.get(
                '/productos',
                {
                  params: {
                    q: texto,
                    page: 1,
                    limit: 6,
                    orden:
                      'relevancia',
                  },
                  signal:
                    controller.signal,
                }
              );

            if (
              sequence !==
              searchSequence.current
            ) {
              return;
            }

            setSearchResults(
              extractProducts(
                response?.data
              ).slice(0, 6)
            );

            setSearchOpen(true);
          } catch (error) {
            if (
              error?.name !==
                'CanceledError' &&
              error?.name !==
                'AbortError' &&
              error?.code !==
                'ERR_CANCELED'
            ) {
              console.warn(
                '[Header] Búsqueda predictiva:',
                error?.message ||
                error
              );
            }

            if (
              sequence ===
              searchSequence.current
            ) {
              setSearchResults([]);
            }
          } finally {
            if (
              sequence ===
              searchSequence.current
            ) {
              setSearchLoading(false);
            }
          }
        },
        250
      );

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    buscadorHabilitado,
    searchText,
  ]);

  const mostrarLogo =
    Boolean(logoUrl) &&
    !logoError;

  const handleSearch =
    (event) => {
      event?.preventDefault?.();

      const texto =
        String(searchText || '')
          .trim();

      /*
       * El término queda conservado en la URL de /productos,
       * pero limpiamos el Header para que no parezca una búsqueda
       * todavía activa después de navegar.
       */
      setSearchText('');
      setSearchResults([]);
      setSearchOpen(false);

      if (!texto) {
        navigate('/productos');
        return;
      }

      if (
        typeof onSearch ===
        'function'
      ) {
        onSearch(texto);
        return;
      }

      navigate(
        `/productos?q=${encodeURIComponent(
          texto
        )}`
      );
    };

  const selectProduct =
    (product) => {
      const slug =
        String(
          product?.slug || ''
        ).trim();

      if (!slug) {
        return;
      }

      /*
       * Al abrir una ficha desde la vista previa, dejamos
       * nuevamente libre el buscador del Header.
       */
      setSearchText('');
      setSearchResults([]);
      setSearchOpen(false);

      navigate(
        `/producto/${encodeURIComponent(
          slug
        )}`
      );
    };

  const clearSearch = () => {
    setSearchText('');
    setSearchResults([]);
    setSearchOpen(false);
  };

  const searchBox =
    buscadorHabilitado ? (
    <ClickAwayListener
      onClickAway={() =>
        setSearchOpen(false)
      }
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: 420,
            md: 540,
          },
          position: 'relative',
        }}
      >
        <Box
          component="form"
          onSubmit={handleSearch}
          role="search"
        >
          <TextField
            fullWidth
            size="small"
            value={searchText}
            onFocus={() => {
              if (
                String(
                  searchText
                ).trim().length >= 2
              ) {
                setSearchOpen(true);
              }
            }}
            onChange={(event) => {
              setSearchText(
                event.target.value
              );

              if (
                String(
                  event.target.value
                ).trim().length >= 2
              ) {
                setSearchOpen(true);
              }
            }}
            placeholder="Buscar productos..."
            inputProps={{
              'aria-label':
                'Buscar productos',
              autoComplete: 'off',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchLoading ? (
                    <CircularProgress
                      size={18}
                    />
                  ) : (
                    <SearchIcon
                      sx={{
                        color:
                          colorTextoSecundario,
                        fontSize: 21,
                      }}
                    />
                  )}
                </InputAdornment>
              ),

              endAdornment:
                searchText ? (
                  <InputAdornment position="end">
                    <Tooltip title="Limpiar">
                      <IconButton
                        size="small"
                        aria-label="Limpiar búsqueda"
                        onClick={
                          clearSearch
                        }
                      >
                        <ClearIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root':
                {
                  bgcolor: colorFondo,
                  color: colorTexto,
                  borderRadius: 999,
                  pr: 0.5,
                  transition:
                    'all 0.18s ease',

                  '& fieldset': {
                    borderColor:
                      '#e5e7eb',
                  },

                  '&:hover fieldset':
                    {
                      borderColor:
                        '#cbd5e1',
                    },

                  '&.Mui-focused':
                    {
                      bgcolor:
                        colorSuperficie,
                      boxShadow:
                        `0 0 0 3px ${colorPrimario}18`,
                    },

                  '&.Mui-focused fieldset':
                    {
                      borderColor:
                        colorPrimario,
                    },
                },
            }}
          />
        </Box>

        {searchOpen &&
        String(searchText)
          .trim().length >= 2 ? (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              zIndex: 1500,
              top:
                'calc(100% + 8px)',
              left: 0,
              right: 0,
              overflow: 'hidden',
              borderRadius: 2.5,
              border:
                '1px solid #e5e7eb',
              boxShadow:
                '0 18px 48px rgba(15,23,42,0.16)',
              bgcolor:
                colorSuperficie,
            }}
          >
            {searchLoading &&
            searchResults.length ===
              0 ? (
              <Stack
                direction="row"
                spacing={1.2}
                alignItems="center"
                justifyContent="center"
                sx={{
                  py: 2.5,
                  px: 2,
                }}
              >
                <CircularProgress
                  size={18}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      colorTextoSecundario,
                  }}
                >
                  Buscando productos...
                </Typography>
              </Stack>
            ) : searchResults.length >
              0 ? (
              <>
                <Box
                  sx={{
                    px: 1.5,
                    pt: 1.25,
                    pb: 0.6,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        '#9ca3af',
                      fontWeight: 800,
                      textTransform:
                        'uppercase',
                      letterSpacing:
                        '0.04em',
                    }}
                  >
                    Productos encontrados
                  </Typography>
                </Box>

                {searchResults.map(
                  (product, index) => (
                    <React.Fragment
                      key={
                        product?.id ??
                        product?.sku ??
                        index
                      }
                    >
                      {index > 0 ? (
                        <Divider />
                      ) : null}

                      <SearchResult
                        product={
                          product
                        }
                        colorPrimario={
                          colorPrimario
                        }
                        colorTexto={
                          colorTexto
                        }
                        colorTextoSecundario={
                          colorTextoSecundario
                        }
                        colorOferta={
                          colorOferta
                        }
                        monedaDefault={
                          moneda
                        }
                        locale={
                          locale
                        }
                        mostrarSku={
                          mostrarSku
                        }
                        mostrarUnidad={
                          mostrarUnidad
                        }
                        mostrarPrecioNormalOferta={
                          mostrarPrecioNormalOferta
                        }
                        mostrarPorcentajeOferta={
                          mostrarPorcentajeOferta
                        }
                        onSelect={() =>
                          selectProduct(
                            product
                          )
                        }
                      />
                    </React.Fragment>
                  )
                )}

                <Divider />

                <Button
                  fullWidth
                  onClick={
                    handleSearch
                  }
                  startIcon={
                    <SearchIcon />
                  }
                  sx={{
                    justifyContent:
                      'flex-start',
                    borderRadius: 0,
                    px: 2,
                    py: 1.25,
                    color:
                      colorPrimario,
                    textTransform:
                      'none',
                    fontWeight: 800,
                  }}
                >
                  Ver todos los resultados para “
                  {String(
                    searchText
                  ).trim()}
                  ”
                </Button>
              </>
            ) : (
              <Box
                sx={{
                  py: 2.5,
                  px: 2,
                  textAlign:
                    'center',
                }}
              >
                <Typography
                  sx={{
                    color:
                      '#374151',
                    fontWeight: 800,
                  }}
                >
                  Sin coincidencias
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.35,
                    color:
                      '#9ca3af',
                  }}
                >
                  Prueba con otro nombre, SKU o código.
                </Typography>
              </Box>
            )}
          </Paper>
        ) : null}
      </Box>
    </ClickAwayListener>
  ) : null;

  const avisoEsInterno =
    barraAvisoEnlace.startsWith('/');

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor:
            colorSuperficie,
          color:
            colorTexto,
          borderBottom:
            '1px solid #e5e7eb',
        }}
      >
        {barraAvisoHabilitada ? (
          <Box
            sx={{
              bgcolor:
                colorPrimario,
              color: '#fff',
            }}
          >
            <Container
              maxWidth="xl"
              sx={{
                minHeight: 34,
                py: 0.55,
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  'center',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                useFlexGap
                flexWrap="wrap"
                textAlign="center"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'inherit',
                    fontWeight: 800,
                  }}
                >
                  {barraAvisoTexto}
                </Typography>

                {barraAvisoEnlace ? (
                  <Link
                    {...(
                      avisoEsInterno
                        ? {
                            component:
                              RouterLink,
                            to:
                              barraAvisoEnlace,
                          }
                        : {
                            href:
                              barraAvisoEnlace,
                            target:
                              '_blank',
                            rel:
                              'noopener noreferrer',
                          }
                    )}
                    underline="always"
                    sx={{
                      color: 'inherit',
                      fontSize: 12,
                      fontWeight: 900,
                      textUnderlineOffset:
                        '2px',
                    }}
                  >
                    Ver más
                  </Link>
                ) : null}
              </Stack>
            </Container>
          </Box>
        ) : null}

        {(publicPages.length > 0 || seguimientoHabilitado) ? (
          <Box
            component="nav"
            aria-label="Accesos rápidos"
            sx={{
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #eef0f2',
            }}
          >
            <Container maxWidth="xl">
              <Stack
                direction="row"
                spacing={0}
                alignItems="center"
                sx={{
                  minHeight: 31,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {publicPages.map((page, index) => (
                  <React.Fragment key={page.id || page.slug}>
                    {index > 0 ? (
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          mx: 0.35,
                          color: '#cbd5e1',
                          fontSize: 11,
                        }}
                      >
                        ·
                      </Box>
                    ) : null}

                    <Link
                      component={RouterLink}
                      to={`/${encodeURIComponent(page.slug)}`}
                      underline="none"
                      sx={{
                        px: 0.8,
                        py: 0.45,
                        color: routeIs(`/${page.slug}`)
                          ? colorPrimario
                          : colorTextoSecundario,
                        fontSize: 11.5,
                        fontWeight: routeIs(`/${page.slug}`) ? 900 : 750,
                        '&:hover': {
                          color: colorPrimario,
                        },
                      }}
                    >
                      {compactPageLabel(page)}
                    </Link>
                  </React.Fragment>
                ))}

                {seguimientoHabilitado ? (
                  <>
                    {publicPages.length > 0 ? (
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          mx: 0.35,
                          color: '#cbd5e1',
                          fontSize: 11,
                        }}
                      >
                        ·
                      </Box>
                    ) : null}

                    <Link
                      component={RouterLink}
                      to="/seguimiento-pedido"
                      underline="none"
                      sx={{
                        px: 0.8,
                        py: 0.45,
                        color: routeIs('/seguimiento-pedido')
                          ? colorPrimario
                          : colorTextoSecundario,
                        fontSize: 11.5,
                        fontWeight: routeIs('/seguimiento-pedido') ? 900 : 750,
                        '&:hover': {
                          color: colorPrimario,
                        },
                      }}
                    >
                      Seguimiento de pedido
                    </Link>
                  </>
                ) : null}
              </Stack>
            </Container>
          </Box>
        ) : null}

        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: {
                xs: 68,
                md: 82,
              },
              py: {
                xs: 0.75,
                md: 0,
              },
              gap: {
                xs: 1,
                md: 1.5,
              },
              flexWrap: {
                xs: 'wrap',
                md: 'nowrap',
              },
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              aria-label={`Ir al inicio de ${nombre}`}
              sx={{
                display:
                  'inline-flex',
                alignItems:
                  'center',
                minWidth: 0,
                textDecoration:
                  'none',
                color: 'inherit',
                flexShrink: 0,
              }}
            >
              {mostrarLogo ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={`Logo ${nombre}`}
                  loading="eager"
                  decoding="async"
                  onError={() =>
                    setLogoError(true)
                  }
                  sx={{
                    display: 'block',
                    width: 'auto',
                    height: 'auto',
                    maxWidth: {
                      xs: 145,
                      sm: 175,
                      md: 190,
                    },
                    maxHeight: {
                      xs: 42,
                      sm: 48,
                      md: 50,
                    },
                    objectFit:
                      'contain',
                    objectPosition:
                      'left center',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <StorefrontIcon
                    sx={{
                      color:
                        colorPrimario,
                      fontSize: {
                        xs: 27,
                        md: 31,
                      },
                    }}
                  />

                  <Typography
                    component="span"
                    sx={{
                      color:
                        colorTexto,
                      fontWeight:
                        800,
                      fontSize: {
                        xs: 17,
                        md: 20,
                      },
                      lineHeight: 1.1,
                      overflow:
                        'hidden',
                      textOverflow:
                        'ellipsis',
                      whiteSpace:
                        'nowrap',
                      maxWidth: {
                        xs: 135,
                        sm: 210,
                      },
                    }}
                  >
                    {nombre}
                  </Typography>
                </Stack>
              )}
            </Box>

            {!isMobile ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  px: {
                    sm: 0.75,
                    md: 1,
                  },
                  minWidth: {
                    sm: 180,
                    md: 280,
                  },
                  maxWidth: {
                    md: 470,
                  },
                }}
              >
                {searchBox}
              </Box>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}

            {!isTablet ? (
              <Stack
                component="nav"
                aria-label="Navegación principal"
                direction="row"
                spacing={0.1}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                <Button
                  component={RouterLink}
                  to="/"
                  size="small"
                  startIcon={<HomeIcon />}
                  sx={navButtonSx(routeIs('/'))}
                >
                  Inicio
                </Button>

                <Button
                  component={RouterLink}
                  to="/productos"
                  size="small"
                  startIcon={<ProductsIcon />}
                  sx={navButtonSx(routeIs('/productos'))}
                >
                  Productos
                </Button>

                <Button
                  type="button"
                  size="small"
                  startIcon={<CategoryIcon />}
                  endIcon={<ExpandMoreIcon />}
                  onMouseEnter={openCategories}
                  onMouseLeave={scheduleCatalogClose}
                  onFocus={openCategories}
                  onClick={openCategories}
                  aria-haspopup="true"
                  aria-expanded={categoriesOpen ? 'true' : undefined}
                  aria-controls={categoriesOpen ? 'webstore-categories-menu' : undefined}
                  sx={{
                    ...navButtonSx(
                      routeIs('/categorias') ||
                      routeIs('/categoria')
                    ),
                    '& .MuiButton-endIcon': {
                      ml: 0.25,
                      transform: categoriesOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    },
                  }}
                >
                  Categorías
                </Button>

                <Button
                  type="button"
                  size="small"
                  startIcon={<BrandsIcon />}
                  endIcon={<ExpandMoreIcon />}
                  onMouseEnter={openBrands}
                  onMouseLeave={scheduleCatalogClose}
                  onFocus={openBrands}
                  onClick={openBrands}
                  aria-haspopup="true"
                  aria-expanded={brandsOpen ? 'true' : undefined}
                  aria-controls={brandsOpen ? 'webstore-brands-menu' : undefined}
                  sx={{
                    ...navButtonSx(
                      routeIs('/marcas') ||
                      routeIs('/marca')
                    ),
                    '& .MuiButton-endIcon': {
                      ml: 0.25,
                      transform: brandsOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    },
                  }}
                >
                  Marcas
                </Button>

                {contactoHabilitado ? (
                  <Button
                    component={RouterLink}
                    to="/contacto"
                    size="small"
                    startIcon={<ContactIcon />}
                    sx={navButtonSx(routeIs('/contacto'))}
                  >
                    Contacto
                  </Button>
                ) : null}
              </Stack>
            ) : null}

            {mostrarMercadoLibre ? (
              isTablet ? (
                <Tooltip title="Tienda en Mercado Libre">
                  <IconButton
                    component="a"
                    href={mercadoLibreUrlTienda}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Ir a nuestra tienda en Mercado Libre"
                    sx={{
                      flexShrink: 0,
                      bgcolor: '#fffbea',
                      border: '1px solid #f3d54e',
                      '&:hover': {
                        bgcolor: '#fff5bf',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={mercadoLibreIcon}
                      alt=""
                      aria-hidden="true"
                      sx={{
                        width: 23,
                        height: 23,
                        objectFit: 'contain',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  component="a"
                  href={mercadoLibreUrlTienda}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Ir a nuestra tienda en Mercado Libre"
                  startIcon={
                    <Box
                      component="img"
                      src={mercadoLibreIcon}
                      alt=""
                      aria-hidden="true"
                      sx={{
                        width: 21,
                        height: 21,
                        objectFit: 'contain',
                      }}
                    />
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 850,
                    borderRadius: 2,
                    px: 1.35,
                    color: '#2d3277',
                    bgcolor: '#fffbea',
                    border: '1px solid #f3d54e',
                    '&:hover': {
                      bgcolor: '#fff5bf',
                      borderColor: '#e7c72f',
                    },
                  }}
                >
                  Tienda en Mercado Libre
                </Button>
              )
            ) : null}

            {carritoHabilitado ? (
              isTablet ? (
                <Tooltip
                  title={
                    displayedCartCount > 0
                      ? `Carrito · ${displayedCartCount} ${
                          displayedCartCount === 1
                            ? 'producto'
                            : 'productos'
                        }`
                      : 'Carrito'
                  }
                >
                  <IconButton
                    component={RouterLink}
                    to="/carrito"
                    aria-label={
                      displayedCartCount > 0
                        ? `Ir al carrito, ${displayedCartCount} productos`
                        : 'Ir al carrito'
                    }
                    sx={{
                      color: colorPrimario,
                      flexShrink: 0,
                      bgcolor: routeIs('/carrito')
                        ? `${colorPrimario}0d`
                        : 'transparent',
                    }}
                  >
                    <Badge
                      badgeContent={
                        displayedCartCount
                      }
                      color="error"
                      max={99}
                      invisible={
                        displayedCartCount <= 0
                      }
                    >
                      <ShoppingCartIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  component={RouterLink}
                  to="/carrito"
                  variant={
                    routeIs('/carrito')
                      ? 'contained'
                      : 'outlined'
                  }
                  aria-label={
                    displayedCartCount > 0
                      ? `Ir al carrito, ${displayedCartCount} productos`
                      : 'Ir al carrito'
                  }
                  startIcon={
                    <Badge
                      badgeContent={
                        displayedCartCount
                      }
                      color="error"
                      max={99}
                      invisible={
                        displayedCartCount <= 0
                      }
                    >
                      <ShoppingCartIcon />
                    </Badge>
                  }
                  sx={{
                    minHeight: 40,
                    px: 1.6,
                    borderRadius: 2,
                    flexShrink: 0,
                    textTransform: 'none',
                    fontWeight: 850,
                    borderColor: colorPrimario,
                    color: routeIs('/carrito')
                      ? '#fff'
                      : colorPrimario,
                    bgcolor: routeIs('/carrito')
                      ? colorPrimario
                      : colorSuperficie,
                    boxShadow: 'none',

                    '&:hover': {
                      borderColor: colorPrimario,
                      bgcolor: routeIs('/carrito')
                        ? colorPrimario
                        : `${colorPrimario}0d`,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {displayedCartCount > 0
                    ? `Carrito (${displayedCartCount})`
                    : 'Carrito'}
                </Button>
              )
            ) : null}

            {isMobile &&
            buscadorHabilitado ? (
              <Box
                sx={{
                  order: 10,
                  width: '100%',
                  pb: 0.5,
                }}
              >
                {searchBox}
              </Box>
            ) : null}
          </Toolbar>
        </Container>
      </AppBar>


      <Popper
        id="webstore-categories-menu"
        open={categoriesOpen}
        anchorEl={categoryAnchorEl}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 12,
            },
          },
        ]}
        sx={{
          zIndex: (theme) =>
            theme.zIndex.appBar + 20,
        }}
      >
        <ClickAwayListener
          onClickAway={closeCatalogMenus}
        >
          <Paper
            elevation={0}
            onMouseEnter={cancelCatalogClose}
            onMouseLeave={scheduleCatalogClose}
            sx={dropdownPaperSx}
          >
        <Box sx={{ px: 0.75, pt: 0.4, pb: 1 }}>
          <Typography
            sx={{
              color: colorTexto,
              fontSize: 15,
              fontWeight: 900,
            }}
          >
            Categorías
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colorTextoSecundario,
              fontWeight: 650,
            }}
          >
            Explore el catálogo por tipo de producto
          </Typography>
        </Box>

        <Divider />

        {catalogNavLoading ? (
          <Box
            sx={{
              minHeight: 110,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : categoryItems.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(2, minmax(0, 1fr))',
              gap: 0.35,
              py: 0.8,
            }}
          >
            {categoryItems
              .slice(0, 12)
              .map((item) => (
                <Button
                  key={item.id}
                  component={RouterLink}
                  to={item.path}
                  onClick={closeCatalogMenus}
                  sx={dropdownItemSx}
                >
                  <Stack
                    component="span"
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.nombre}
                    </Box>

                    {item.productos > 0 ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          flexShrink: 0,
                          color: colorTextoSecundario,
                          fontSize: 10.5,
                          fontWeight: 800,
                          opacity: 0.72,
                        }}
                      >
                        {item.productos.toLocaleString(
                          locale || 'es-CL'
                        )}
                      </Typography>
                    ) : null}
                  </Stack>
                </Button>
              ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              py: 2.2,
              px: 0.8,
              color: colorTextoSecundario,
            }}
          >
            No hay categorías disponibles.
          </Typography>
        )}

        <Divider />

        <Button
          component={RouterLink}
          to="/categorias"
          onClick={closeCatalogMenus}
          endIcon={<ArrowForwardIcon />}
          sx={{
            mt: 0.7,
            width: '100%',
            justifyContent: 'space-between',
            px: 1.1,
            borderRadius: 2,
            textTransform: 'none',
            color: colorPrimario,
            fontWeight: 900,
            '&:hover': {
              bgcolor: `${colorPrimario}0b`,
            },
          }}
        >
          Ver todas las categorías
        </Button>

          </Paper>
        </ClickAwayListener>
      </Popper>

      <Popper
        id="webstore-brands-menu"
        open={brandsOpen}
        anchorEl={brandAnchorEl}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 12,
            },
          },
        ]}
        sx={{
          zIndex: (theme) =>
            theme.zIndex.appBar + 20,
        }}
      >
        <ClickAwayListener
          onClickAway={closeCatalogMenus}
        >
          <Paper
            elevation={0}
            onMouseEnter={cancelCatalogClose}
            onMouseLeave={scheduleCatalogClose}
            sx={dropdownPaperSx}
          >
        <Box sx={{ px: 0.75, pt: 0.4, pb: 1 }}>
          <Typography
            sx={{
              color: colorTexto,
              fontSize: 15,
              fontWeight: 900,
            }}
          >
            Marcas
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colorTextoSecundario,
              fontWeight: 650,
            }}
          >
            Encuentre productos por fabricante
          </Typography>
        </Box>

        <Divider />

        {catalogNavLoading ? (
          <Box
            sx={{
              minHeight: 110,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : brandItems.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(2, minmax(0, 1fr))',
              gap: 0.35,
              py: 0.8,
            }}
          >
            {brandItems
              .slice(0, 12)
              .map((item) => (
                <Button
                  key={item.id}
                  component={RouterLink}
                  to={item.path}
                  onClick={closeCatalogMenus}
                  sx={dropdownItemSx}
                >
                  <Stack
                    component="span"
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.nombre}
                    </Box>

                    {item.productos > 0 ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          flexShrink: 0,
                          color: colorTextoSecundario,
                          fontSize: 10.5,
                          fontWeight: 800,
                          opacity: 0.72,
                        }}
                      >
                        {item.productos.toLocaleString(
                          locale || 'es-CL'
                        )}
                      </Typography>
                    ) : null}
                  </Stack>
                </Button>
              ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              py: 2.2,
              px: 0.8,
              color: colorTextoSecundario,
            }}
          >
            No hay marcas disponibles.
          </Typography>
        )}

        <Divider />

        <Button
          component={RouterLink}
          to="/marcas"
          onClick={closeCatalogMenus}
          endIcon={<ArrowForwardIcon />}
          sx={{
            mt: 0.7,
            width: '100%',
            justifyContent: 'space-between',
            px: 1.1,
            borderRadius: 2,
            textTransform: 'none',
            color: colorPrimario,
            fontWeight: 900,
            '&:hover': {
              bgcolor: `${colorPrimario}0b`,
            },
          }}
        >
          Ver todas las marcas
        </Button>

          </Paper>
        </ClickAwayListener>
      </Popper>

      <BotonContactoFlotante
          tienda={source}
        />
    </>
  );
}
